import { useRef, useState } from 'react';
import { BrainCircuit, Check, LoaderCircle, RotateCcw } from 'lucide-react';
import { WorkspaceDialog } from './WorkspaceDialog';
import './reset-learning.css';

export type ResetLearningSelection = Readonly<{
  jobId: string;
  typeId: string;
  typeName: string;
  scope: 'field' | 'document-type' | 'line-items';
  fieldId?: string;
  fieldName?: string;
  expectedSchemaVersion: number;
}>;

export function ResetLearningDialog({
  selection,
  onReset,
  onClose,
}: {
  selection: ResetLearningSelection;
  onReset: (selection: ResetLearningSelection) => Promise<void>;
  onClose: () => void;
}) {
  // Freeze the confirmation's target. Changes in the underlying setup must not
  // silently retarget a destructive action while its dialog is open.
  const [target] = useState(() => ({ ...selection }));
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submitting = useRef(false);
  // A type's reset starts only its recognition over (its fields and line items keep what
  // they learned), and a field's takes the type's tuned layout reader (learned from all its fields): each
  // says what it starts over.
  const name =
    target.scope === 'field'
      ? target.fieldName
      : target.scope === 'line-items'
        ? 'Line items'
        : 'Recognition';
  const restarts =
    target.scope === 'document-type'
      ? `How ${target.typeName} is recognized starts over. What its fields and line items learned stays.`
      : target.scope === 'field'
        ? `Start its learning over. If ${target.typeName} has a tuned layout reader, it starts over too.`
        : 'Start its learning over.';
  const close = () => {
    if (!submitting.current) onClose();
  };
  const reset = async () => {
    if (!acknowledged || submitting.current || complete) return;
    submitting.current = true;
    setBusy(true);
    setError(null);
    try {
      await onReset(target);
      setComplete(true);
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : 'Learning could not be reset. Try again.',
      );
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  };
  return (
    <WorkspaceDialog
      telemetry="reset-learning"
      title={complete ? 'Ready to learn again' : 'Reset learning'}
      onClose={close}
      compact
    >
      <div className="reset-learning-content" aria-busy={busy}>
        <div className="reset-learning-target">
          {complete ? (
            <Check size={34} aria-hidden="true" />
          ) : (
            <BrainCircuit size={34} aria-hidden="true" />
          )}
          <div>
            <small>{target.typeName}</small>
            <h3>{name}</h3>
          </div>
        </div>
        {complete ? (
          <p role="status">
            New confirmations will teach this again. Your saved documents and values are unchanged.
          </p>
        ) : (
          <>
            <p>{restarts} Saved documents, values and review history stay.</p>
            <label className="reset-learning-acknowledgement">
              <input
                type="checkbox"
                checked={acknowledged}
                disabled={busy}
                onChange={(event) => setAcknowledged(event.target.checked)}
              />
              <span>
                Forget what it learned for{' '}
                <strong>{target.scope === 'document-type' ? target.typeName : name}</strong>. I’ll
                teach it again with new confirmations.
              </span>
            </label>
          </>
        )}
        {error && (
          <p role="alert" data-telemetry="error:reset-learning-failed">
            {error}
          </p>
        )}
        <div className="reset-learning-actions">
          {complete ? (
            <button className="primary-button" onClick={close}>
              Done
            </button>
          ) : (
            <>
              <button className="secondary-button" disabled={busy} onClick={close}>
                Keep learning
              </button>
              <button
                className="primary-button"
                disabled={!acknowledged || busy}
                onClick={() => void reset()}
              >
                {busy ? (
                  <LoaderCircle size={18} aria-hidden="true" />
                ) : (
                  <RotateCcw size={18} aria-hidden="true" />
                )}
                {busy ? 'Resetting…' : 'Reset learning'}
              </button>
            </>
          )}
        </div>
      </div>
    </WorkspaceDialog>
  );
}
